import { useMediaQuery } from '@mui/material'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { Box } from '@mui/system'
import PropTypes from 'prop-types'
import { useRemakesQuery } from 'Queries/Maps'
import { getRemakeImage } from 'Queries/utils'
import React, { useState } from 'react'
import { Else, If, Then, When } from 'react-if'
import { CONTENT_TYPES } from 'Scenes/constants'
import FAIcon from 'Theme/FAIcon'
import { FA_ICONS } from 'Theme/font-awesome-icon-constants'

import { navigateToLoginModal } from '../../Scenes/navigators'
import { BREAKPOINT_MIN_SM } from '../../Theme/stylesUtil'
import { useAuth } from '../Auth'
import PrefabRatingModal from './ContentPreview/PrefabRating/PrefabRatingModal'
import GameTypeIcon from './GameTypeIcon'

const SkeletonPlaceholder = () => (
  <Skeleton
    animation={false}
    width={32}
  />
)

const DetailsIcon = ({ iconClass }) => (
  <FAIcon
    iconFontSize={14}
    boxSize={14}
    iconClassName={iconClass}
    style={{ marginRight: '4px' }}
  />
)

const columnStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '50%',
  paddingRight: '8px',
}

const rowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  margin: 0,
  '& > *': {
    flexShrink: 0,
  },
}

const ContentEntityDetails = (props) => {
  const {
    type,
    gameTitle,
    remakeId,
    playersMin,
    playersMax,
    gameTypeNames,
    totalPlayed,
    recentlyPlayed,
    timeUpdated,
    timePublished,
    contentRatings,
    contentId,
  } = props
  const { isAuthorized } = useAuth()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [rating, setRating] = useState(3)
  const { data: remakes = [] } = useRemakesQuery()
  const mapRemake = remakes.find((r) => r.id === remakeId)

  const isBiggerThanSMScreen = useMediaQuery(BREAKPOINT_MIN_SM)
  const handleOpenRatingModal = () => {
    if (!isAuthorized) return navigateToLoginModal()
    setIsOpenModal(true)
  }
  const handleCloseRatingModal = () => {
    setIsOpenModal(false)
  }

  return (
    <Box
      component={Paper}
      sx={(theme) => ({
        ...theme.typography.caption,
        color: theme.palette.text.secondary,
        display: 'flex',
        gap: '16px',
        padding: '16px',
        [theme.breakpoints.up('smd')]: {
          flexDirection: 'column',
        },
      })}
    >
      <Box sx={columnStyles}>
        <When condition={type === CONTENT_TYPES.PREFABS && !isBiggerThanSMScreen}>
          <Box sx={rowStyles}>
            <DetailsIcon iconClass={FA_ICONS.FILLED_STAR} />

            <span style={{ fontWeight: 'bold' }}>{contentRatings / 2 || 0}</span>
            <Typography
              sx={{ marginLeft: '10px' }}
              variant="caption"
              color="secondary.main"
              onClick={handleOpenRatingModal}
            >
              Rate Prefab
            </Typography>
          </Box>
        </When>

        <Box sx={rowStyles}>
          <DetailsIcon iconClass={FA_ICONS.GAMEPAD} />
          <If condition={!!gameTitle}>
            <Then>
              <span>{gameTitle}</span>
            </Then>
            <Else>
              <SkeletonPlaceholder />
            </Else>
          </If>
        </Box>

        <When condition={!!gameTypeNames?.length}>
          {gameTypeNames.map((name) => (
            <Box
              key={name}
              sx={rowStyles}
            >
              <GameTypeIcon
                gameType={name}
                boxSize={18}
                style={{ justifyContent: 'flex-start' }}
              />
              <span>{name}</span>
            </Box>
          ))}
        </When>

        <When condition={!!playersMin && !!playersMax}>
          <Box sx={rowStyles}>
            <DetailsIcon iconClass={FA_ICONS.USER_GROUP} />
            <span>{`${playersMin}-${playersMax}`}</span>
          </Box>
        </When>

        <When condition={!!mapRemake?.name}>
          <Box sx={rowStyles}>
            <img
              alt={mapRemake?.name}
              src={getRemakeImage(mapRemake)}
              style={{ maxHeight: '14px' }}
            />
            <span>{mapRemake?.name}</span>
          </Box>
        </When>
      </Box>

      <Box
        sx={(theme) => ({
          ...columnStyles,
          [theme.breakpoints.down('smd')]: {
            order: -1,
          },
        })}
      >
        <When condition={type === CONTENT_TYPES.MAPS}>
          <Box sx={rowStyles}>
            <DetailsIcon iconClass={FA_ICONS.PLAY} />
            <If condition={!!totalPlayed}>
              <Then>
                <span style={{ fontWeight: 'bold' }}>{totalPlayed}</span>
              </Then>
              <Else>
                <SkeletonPlaceholder />
              </Else>
            </If>
            <span>Total played</span>
          </Box>
        </When>

        <When condition={type === CONTENT_TYPES.MAPS}>
          <Box sx={rowStyles}>
            <DetailsIcon iconClass={FA_ICONS.CLOCK_ROTATE_LEFT} />
            <If condition={!!recentlyPlayed}>
              <Then>
                <span style={{ fontWeight: 'bold' }}>{recentlyPlayed}</span>
              </Then>
              <Else>
                <SkeletonPlaceholder />
              </Else>
            </If>
            <span>Recently played</span>
          </Box>
        </When>

        <Box sx={rowStyles}>
          <DetailsIcon iconClass={FA_ICONS.CALENDAR_LINES_PEN} />
          <If condition={!!timeUpdated}>
            <Then>
              <span style={{ fontWeight: 'bold' }}>{timeUpdated}</span>
            </Then>
            <Else>
              <SkeletonPlaceholder />
            </Else>
          </If>
          <span>Modified</span>
        </Box>

        <Box sx={rowStyles}>
          <DetailsIcon iconClass={FA_ICONS.CALENDAR_LINES} />
          <If condition={!!timePublished}>
            <Then>
              <span style={{ fontWeight: 'bold' }}>{timePublished}</span>
            </Then>
            <Else>
              <SkeletonPlaceholder />
            </Else>
          </If>
          <span>Published</span>
        </Box>
      </Box>
      <PrefabRatingModal
        isOpenModal={isOpenModal}
        handleCloseModal={handleCloseRatingModal}
        contentId={contentId}
        rating={rating}
        setRating={setRating}
      />
    </Box>
  )
}

ContentEntityDetails.propTypes = {
  likes: PropTypes.number,
  views: PropTypes.number,
  recentlyPlayed: PropTypes.number,
  totalPlayed: PropTypes.number,
  timeUpdated: PropTypes.string,
  timePublished: PropTypes.string,
  gameTitle: PropTypes.string,
  primaryGameTypeId: PropTypes.number,
  remakeId: PropTypes.number,
  playersMin: PropTypes.number,
  playersMax: PropTypes.number,
  type: PropTypes.oneOf([CONTENT_TYPES.MAPS, CONTENT_TYPES.PREFABS]),
}

export default ContentEntityDetails
